import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { ContextualMenuItemType, IconButton } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';

import { CustomView } from '../../../api-client';
import { RouterSettings } from '../../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useCustomView } from './hooks/useCustomView';
import { useClassNames } from './setting-custom-view-page.classNames';

import { IAIApplicationLayout } from '../../../common/layouts/IAIApplicationLayout';
import { IAIBreadcrumb } from '../../../common/components/IAIBreadcrumb';
import {
    IAIDetailsList,
    IAIContextualMenuColumn,
    IAIDatetimeColumn,
    IAIStringColumn
} from '../../../common/components/IAITable';
import FormDrawer from './components/FormDrawer/form-drawer';
import DeleteDialog from './components/DeleteDialog/delete-dialog';
import { DataEnchanter } from '../../../utils/data-enchanters';

const SettingCustomViewPage: React.FunctionComponent = () => {
    const {
        settingCustomViewPageContainer
    } = useClassNames();

    const {
        handleCustomView,
        customViews,
        customViewsLoading,
        handleCreateCustomView,
        createCustomViewLoading,
        handleUpdateCustomView,
        updateCustomViewLoading,
        handleDeleteCustomView,
        deleteCustomViewLoading
    } = useCustomView();

    useEffect(() => {
        handleCustomView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _handleSearchPredicate = (dataItem: CustomView, searchKey: string) => (
        _.includes(dataItem.displayName?.toLowerCase(), searchKey)
    );

    //#region Form-Management

    const [customViewDrawerVisible, { toggle: toggleCustomViewDrawerVisible }] = useBoolean(false);
    const [deleteCustomViewVisible, { toggle: toggleDeleteCustomViewVisible }] = useBoolean(false);
    const [delegateCustomView, setDelegateCustomView] = useState<CustomView>();

    const _handleCreateAction = () => {
        setDelegateCustomView(undefined);
        toggleCustomViewDrawerVisible();
    }

    const _handleUpsertAction = (dataItem: CustomView) => {
        setDelegateCustomView(dataItem);
        toggleCustomViewDrawerVisible();
    }

    const _handleDismissUpsertAction = () => {
        setDelegateCustomView(undefined);
        toggleCustomViewDrawerVisible();
    }

    const _handleDeleteAction = (dataItem: CustomView) => {
        setDelegateCustomView(dataItem);
        toggleDeleteCustomViewVisible();
    }

    const _handleDismissDeleteAction = () => {
        setDelegateCustomView(undefined);
        toggleDeleteCustomViewVisible();
    }

    const _handleUpsertCustomView = (dataItem: CustomView) => {
        const onUpsertSuccess = () => {
            _handleDismissUpsertAction();

            handleCustomView();
        };

        dataItem.id
            ?
            handleUpdateCustomView({ id: dataItem.id, customView: dataItem, onSuccess: onUpsertSuccess })
            :
            handleCreateCustomView({ customView: dataItem, onSuccess: onUpsertSuccess });
    }

    const _handleDeleteCustomView = () => {
        if (delegateCustomView?.id) {
            handleDeleteCustomView({
                id: delegateCustomView.id,
                onSuccess: () => {
                    _handleDismissDeleteAction();

                    handleCustomView();
                }
            });
        }
    }

    //#endregion

    const {
        t: translate
    } = useTranslation();

    return (
        <IAIApplicationLayout
            icon={'ArrangeBringToFront'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={translate(TRANSLATION_KEYS.SecondaryHeading)}
        >
            <div className={settingCustomViewPageContainer}>
                <IAIBreadcrumb
                    items={[
                        {
                            key: RouterSettings.ROUTES_KEYS.IAI_APP_SETTING,
                            text: translate(TRANSLATION_KEYS.BreadcrumbSettingHeading)
                        },
                        {
                            key: RouterSettings.ROUTES_KEYS.IAI_APP_SETTING_AREA,
                            text: translate(TRANSLATION_KEYS.Heading),
                            isCurrentItem: true
                        }
                    ]}
                />

                <IAIDetailsList
                    dataSource={customViews || []}
                    dataColumns={[
                        {
                            key: 'view__Name',
                            fieldName: 'name',
                            name:  translate(TRANSLATION_KEYS.ColumnViewNameLabel),
                            minWidth: 150,
                            maxWidth: 150,
                            onRender: (dataItem: CustomView) => (
                                <IAIStringColumn align="start" text={dataItem.displayName} />
                            )
                        },
                        {
                            key: 'view__AssignedTo',
                            fieldName: 'assignedTo',
                            name: translate(TRANSLATION_KEYS.ColumnAssignedToLabel),
                            minWidth: 100,
                            maxWidth: 100,
                            isResizable: true,
                            onRender: (dataItem: CustomView) => (
                                <div className="text-center">
                                    <IconButton
                                        iconProps={
                                            {
                                                iconName: dataItem.selfAssigned
                                                    ? 'CheckboxCompositeReversed'
                                                    : 'Checkbox'
                                            }
                                        }
                                    />
                                </div>
                            )
                        },
                        {
                            key: 'view__Status',
                            fieldName: '',
                            name: translate(TRANSLATION_KEYS.ColumnStatusLabel),
                            minWidth: 300,
                            maxWidth: 400,
                            isResizable: true,
                            onRender: (dataItem: CustomView) => (
                                <IAIStringColumn
                                    align="start"
                                    text={_.map(dataItem.status, entry => translate(DataEnchanter.textByStatusEnum(entry ?? ''))).join(', ')}
                                />
                            )
                        },
                        {
                            key: 'view__Period',
                            fieldName: 'timePeriod',
                            name: translate(TRANSLATION_KEYS.ColumnTimePeriodLabel),
                            minWidth: 150,
                            maxWidth: 150,
                            onRender: (dataItem: CustomView) => (
                                <IAIStringColumn align="start" text={translate(DataEnchanter.textByTimePeriodTypeEnum(dataItem.timePeriod ?? ''))} />
                            )
                        },
                        {
                            key: 'view__CreatedAt',
                            fieldName: 'createdAt',
                            name: translate(TRANSLATION_KEYS.ColumnCreatedAtLabel),
                            minWidth: 100,
                            onRender: (dataItem: CustomView) => (
                                <IAIDatetimeColumn align="start" datetime={dataItem.createdAt} />
                            )
                        },
                        {
                            key: 'view__actions',
                            fieldName: '',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (dataItem: CustomView, dataIndex) => (
                                <IAIContextualMenuColumn
                                    menuItems={[
                                        {
                                            key: `menuItem__update_${dataIndex}`,
                                            iconProps: { iconName: 'EditCreate' },
                                            text: translate(TRANSLATION_KEYS.ColumnActionCommandUpdateLabel),
                                            title: translate(TRANSLATION_KEYS.ColumnActionCommandUpdateTooltip),
                                            onClick: () => _handleUpsertAction(dataItem)
                                        },
                                        { key: `menuItem__divider_${dataIndex}`, itemType: ContextualMenuItemType.Divider },
                                        {
                                            key: `menuItem__delete_${dataIndex}`,
                                            iconProps: { iconName: 'Delete' },
                                            text: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteLabel),
                                            title: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteTooltip),
                                            onClick: () => _handleDeleteAction(dataItem)
                                        }
                                    ]}
                                />
                            )
                        }
                    ]}
                    dataLoading={customViewsLoading}
                    commands={[
                        {
                            key: '',
                            disabled: customViewsLoading,
                            text: translate(TRANSLATION_KEYS.CommandCreateLabel),
                            title: translate(TRANSLATION_KEYS.CommandCreateTooltip),
                            iconProps: { iconName: 'Add' },
                            onClick: _handleCreateAction
                        }
                    ]}
                    emptyContentLabel={translate(TRANSLATION_KEYS.DetailsListEmptyContentLabel)}
                    onItemInvoked={(dataItem: CustomView) => _handleUpsertAction(dataItem)}
                    onSearchPredicate={_handleSearchPredicate}
                />

                <FormDrawer
                    customView={delegateCustomView}
                    disabled={customViewsLoading || createCustomViewLoading || updateCustomViewLoading}
                    drawerVisible={customViewDrawerVisible}
                    onDrawerConfirm={_handleUpsertCustomView}
                    onDrawerDismiss={_handleDismissUpsertAction}
                />

                <DeleteDialog
                    disabled={customViewsLoading || deleteCustomViewLoading}
                    modalVisible={deleteCustomViewVisible}
                    onModalConfirm={_handleDeleteCustomView}
                    onModalDismiss={_handleDismissDeleteAction}
                />
            </div>
        </IAIApplicationLayout>
    );
}

export default SettingCustomViewPage;